import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import DebtStatus from "./debt-status.component";
import formatCurrency from "../../core/helpers/format-currency.helper";
import Divider from "@mui/material/Divider";
import AccumulatedDebtInterface from "@homegame/common/dist/interface/accumulated-debt.interface";
import { useState } from "react";
import Button from "@mui/material/Button";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import UserDebtStatus from "@homegame/common/dist/enum/user-debt-status.enum";
import PaidIcon from "@mui/icons-material/Paid";
import DoneIcon from "@mui/icons-material/Done";
import ClearIcon from "@mui/icons-material/Clear";
import TableInterface from "@homegame/common/dist/interface/table.interface";
import { useAuth } from "../../core/context/auth.context";
import ConfirmationDialogComponent from "../../core/components/confirmation-dialog.component";
import UserDebtsService from "../../core/service/user-debts.service";
import { useToast } from "../../core/hooks/use-toast.hook";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import { Link, useParams } from "react-router-dom";

const useStyles = makeStyles(() =>
  createStyles({
    title: {
      color: "#fff",
      fontSize: 15,
      fontWeight: 400,
      textShadow: "none",
    },
    value: {
      width: "100%",
      fontSize: 12,
      color: "#fff",
      textShadow: "none",
      fontWeight: 300,
      "& svg": {
        fontSize: 14,
        marginBottom: -3,
        color: "#fff",
      },
    },
    price: {
      color: "#fff",
      textShadow: "none",
      fontSize: 30,
      textAlign: "center",
    },
  }),
);

const getTableName = (table?: TableInterface) => {
  if (!table) return "";
  if (!table.deletedAt) return table.name;
  return (
    <>
      {`${table.name}`}
      <span style={{ color: "red" }}> (archived)</span>
    </>
  );
};

export default function AccumulatedDebtItemComponent({
  debt,
  type,
  onAction,
}: {
  debt: AccumulatedDebtInterface;
  type?: "users" | "to-user";
  onAction: () => any;
}) {
  const classes = useStyles({});

  const { user } = useAuth();
  const userDebtsService = new UserDebtsService();
  const { showToast } = useToast();
  const { tabName } = useParams();

  const [dialogState, setDialogState] = useState<{
    open: boolean;
    actionType: "approve" | "decline" | "pay";
    debtItem: AccumulatedDebtInterface | undefined;
  }>({
    open: false,
    actionType: "pay",
    debtItem: undefined,
  });

  const handleDialogOpen = (
    debtListItem: AccumulatedDebtInterface,
    actionType: "approve" | "decline" | "pay",
  ) => {
    setDialogState({
      open: true,
      actionType: actionType,
      debtItem: debtListItem,
    });
  };

  const onConfirmationClose = () =>
    setDialogState({
      open: false,
      actionType: "pay",
      debtItem: undefined,
    });

  const getConfirmationText = () => {
    let text = `Are you sure you want to ${dialogState.actionType}`;

    if (["approve", "decline"].includes(dialogState.actionType))
      text += ` payment from ${dialogState.debtItem?.fromUser?.name}`;

    let tableName = dialogState.debtItem?.table?.name;
    if (dialogState.actionType === "pay") {
      text += ` to ${dialogState.debtItem?.toUser?.name}`;
    }

    text += ` in table ${tableName}?`;
    return text;
  };

  const onPaymentConfirmed = async () => {
    try {
      if (dialogState.actionType === "decline") {
        await userDebtsService.declineTableDebt({
          ...dialogState.debtItem!,
          amount: dialogState.debtItem?.accumulatedDebt!,
        });
        showToast("Debt declined!", "success");
      } else if (dialogState.actionType === "approve") {
        await userDebtsService.approveTableDebt({
          ...dialogState.debtItem!,
          amount: dialogState.debtItem?.accumulatedDebt!,
        });
        showToast("Debt approved!", "success");
      } else if (dialogState.actionType === "pay") {
        await userDebtsService.payTableDebt({
          ...dialogState.debtItem!,
          amount: dialogState.debtItem?.accumulatedDebt!,
        });
        showToast("Debt payed!", "success");
      }
    } catch (e) {
      showToast((e as Error).message, "error");
    } finally {
      onConfirmationClose();
      if (onAction) onAction();
    }
  };

  const OptionsMenu = ({ debt }: { debt: AccumulatedDebtInterface }) => {
    const handleButtonClick = (
      debtListItem: AccumulatedDebtInterface,
      actionType: "approve" | "decline" | "pay",
    ) => {
      handleDialogOpen(debtListItem, actionType);
    };

    return (
      <>
        <Button
          color="info"
          variant="outlined"
          style={{ textShadow: "none" }}
          component={Link}
          to={`/transactions/${tabName || "my-debts"}/${debt.tableId}/${debt.fromUserId}/${debt.toUserId}`}
          startIcon={<InfoOutlinedIcon />}
        >
          Info
        </Button>

        {user?.id === debt.fromUserId &&
          [UserDebtStatus.OPEN, UserDebtStatus.PARTIAL].includes(
            debt.status,
          ) && (
            <Button
              onClick={() => handleButtonClick(debt, "pay")}
              color="success"
              variant="outlined"
              style={{ textShadow: "none", marginLeft: 10 }}
              startIcon={<PaidIcon />}
            >
              Pay
            </Button>
          )}
        {user?.id === debt.toUserId &&
          debt.status === UserDebtStatus.PENDING && (
            <Button
              onClick={() => handleButtonClick(debt, "approve")}
              color="success"
              variant="outlined"
              style={{ textShadow: "none", marginLeft: 10 }}
              startIcon={<DoneIcon />}
            >
              Accept
            </Button>
          )}

        {user?.id === debt.toUserId &&
          debt.status === UserDebtStatus.PENDING && (
            <Button
              onClick={() => handleButtonClick(debt, "decline")}
              color="error"
              variant="outlined"
              style={{ textShadow: "none", marginLeft: 10 }}
              startIcon={<ClearIcon />}
            >
              Decline
            </Button>
          )}
      </>
    );
  };

  return (
    <Grid container spacing={0}>
      <Grid item xs={7}>
        <Typography className={classes.title}>
          {type === "users" && `Debt to ${debt.toUser?.name}`}
          {type === "to-user" && `${debt.fromUser?.name} owes me`}
        </Typography>
        <Typography className={classes.value}>
          In {getTableName(debt.table)}
        </Typography>
      </Grid>

      <Grid
        item
        xs={2}
        justifyContent="space-between"
        display="flex"
        alignItems="center"
      >
        <DebtStatus status={debt.status} showIcons />
      </Grid>

      <Grid
        item
        xs={2}
        justifyContent="space-between"
        display="flex"
        alignItems="center"
      >
        <Typography className={classes.price}>
          {formatCurrency(debt.accumulatedDebt, debt.currency)}
        </Typography>
      </Grid>

      <Grid item mb={2} mt={1} xs={12}>
        <Divider style={{ borderColor: "rgba(238,238,238, 0.2)" }} />
      </Grid>
      <Grid
        item
        p={0}
        m={0}
        mb={1}
        xs={12}
        width="100%"
        display="flex"
        justifyContent="center"
      >
        <OptionsMenu debt={debt} />
      </Grid>
      <ConfirmationDialogComponent
        text={getConfirmationText()}
        open={dialogState.open}
        onClose={onConfirmationClose}
        actionText={`${dialogState.actionType}`}
        action={onPaymentConfirmed}
      />
    </Grid>
  );
}
